import React, {
  ElementType,
  FC,
  useCallback,
  useEffect,
  useState,
} from "react";
import { usePopper } from "react-popper";
import styled, { css } from "styled-components";
import {
  Button,
  ButtonProps,
  propStyling,
  PropsWithPropStyling,
  tombac,
  Tooltip,
} from "tombac";
import {
  AppsDashboardIcon,
  EmailIcon,
  HomeIcon,
  Support2Icon,
  SupportIcon,
  TtLogoIcon,
} from "tombac-icons";
import { renderProductIcon } from "../../../icons";
import useOutsideClick from "../../../useOutsideClick";
import {
  useMeDetailsQuery,
  UserProduct,
  UserProductFallback,
  UserProductSafe,
} from "../../legolandApi";
import { useLegoland } from "../../LegolandContext";
import { TrackingFunction } from "../LegolandNavbarTypes";
import { ProductTile } from "./ProductTile";

export interface LegolandNavbarAppsProps {
  linkComponent?: ElementType;
  track: TrackingFunction;
}

export const LegolandNavbarApps: FC<LegolandNavbarAppsProps> = ({
  linkComponent = "a",
  track,
}) => {
  const { legolandUiUrl, productId } = useLegoland();
  const meDetailsQuery = useMeDetailsQuery();

  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);

  const { styles, attributes, forceUpdate } = usePopper(
    referenceElement,
    popperElement,
    {
      modifiers: [
        { name: "arrow", options: { element: arrowElement } },
        { name: "offset", options: { offset: [0, 16] } },
        { name: "preventOverflow", options: { padding: 8 } },
      ],
      placement: "bottom",
    }
  );

  const [isOpen, setIsOpen] = useState(false);
  const ref = React.useRef<HTMLButtonElement | null>(null);
  ref.current = referenceElement;
  useOutsideClick(
    referenceElement,
    useCallback(() => setIsOpen(false), [])
  );

  useEffect(() => {
    if (forceUpdate) setTimeout(forceUpdate);
  }, [forceUpdate, isOpen, popperElement]);

  if (!meDetailsQuery.data?.meDetails) return null;

  const products: (UserProduct | UserProductFallback)[] =
    meDetailsQuery.data.meDetails.products.sort(compareProductOrder);

  return (
    <Container>
      <Tooltip
        content={<TooltipContent>Move apps</TooltipContent>}
        placement="bottom"
        size="s"
        variant="inverted"
      >
        {(tooltip) => (
          <Button
            $borderLeft="1u solid --neutral-300"
            $borderRadius="0"
            $h="100%"
            $w="100%"
            className="LegolandNavbar__apps-toggle"
            labelProps={{ $alignItems: "center", $display: "flex" }}
            onClick={() => {
              tooltip.close();
              setIsOpen((isOpen) => !isOpen);
            }}
            onMouseEnter={() => !isOpen && tooltip.open()}
            onMouseLeave={tooltip.close}
            ref={(el) => {
              setReferenceElement(el);
              tooltip.ref(el);
            }}
            shape="circle"
            theme={{
              hover: { backgroundColor: "--neutral-300", color: "--black" },
              ripple: { circleFill: "--white" },
            }}
            variant="flat"
          >
            {productId ? (
              <>
                {renderProductIcon(productId)}
                <DropIcon />
              </>
            ) : (
              <AppsDashboardIcon />
            )}
          </Button>
        )}
      </Tooltip>
      {isOpen && (
        <Popup
          isOpen={isOpen}
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
        >
          <PopupArrow ref={setArrowElement} style={styles.arrow} />
          <PopupHeading>My products</PopupHeading>
          <ProductList>
            {products.map((p) => {
              if (p.access.status === "DENIED") return null;
              return (
                <ProductListItem key={p.id}>
                  <ProductTile
                    isActive={p.id === productId}
                    product={p}
                    track={track}
                  />
                </ProductListItem>
              );
            })}
          </ProductList>
          <PopupHeading $mt="4sp">Quick links</PopupHeading>
          <QuickLinkList>
            <QuickLinkItem>
              <QuickLink
                forwardedAs={linkComponent}
                href={`${legolandUiUrl}/dashboard`}
                onClick={() => track("top navigation", "move dashboard")}
                prepend={<HomeIcon />}
              >
                MOVE Dashboard
              </QuickLink>
            </QuickLinkItem>
            <QuickLinkItem>
              <QuickLink
                forwardedAs={linkComponent}
                href="https://www.tomtom.com/products/road-traffic-data-analytics/"
                prepend={<TtLogoIcon />}
                target="_blank"
              >
                TomTom Road Analytics
              </QuickLink>
            </QuickLinkItem>
            <QuickLinkItem>
              <QuickLink
                forwardedAs={linkComponent}
                href={`${legolandUiUrl}/dashboard/contact/sales`}
                prepend={<EmailIcon />}
                target={productId ? "_blank" : undefined}
              >
                Contact with Sales Team
              </QuickLink>
            </QuickLinkItem>
            <QuickLinkItem>
              <QuickLink
                forwardedAs={linkComponent}
                href={`${legolandUiUrl}/dashboard/contact/support`}
                prepend={<SupportIcon />}
                target={productId ? "_blank" : undefined}
              >
                Contact with Support Team
              </QuickLink>
            </QuickLinkItem>
            <QuickLinkItem>
              <QuickLink
                forwardedAs={linkComponent}
                href="https://support.move.tomtom.com/"
                prepend={<Support2Icon />}
                target="_blank"
              >
                User guides &amp; FAQ
              </QuickLink>
            </QuickLinkItem>
          </QuickLinkList>
        </Popup>
      )}
    </Container>
  );
};

const Container = styled.div`
  height: 100%;
  min-width: ${tombac.space(10)};
  position: relative;

  &:hover:before {
    bottom: ${tombac.space(-2)};
    left: auto;
    right: ${tombac.space(2)};
  }

  &:hover:after {
    bottom: ${tombac.space(1)};
    left: auto;
    right: ${tombac.space(4)};
  }
`;

const DropIcon = styled.span`
  &::after {
    content: "▾";
    margin-left: ${tombac.space(0.5)};
  }
`;

const Popup = styled.div<{ isOpen: boolean }>`
  background-color: ${tombac.color.white};
  box-shadow: ${tombac.unit(0, 8, 40, 4)} hsla(204, 33%, 16%, 0.15);
  margin-bottom: ${tombac.space(1)};
  padding: ${tombac.space(3)};

  ${({ isOpen }) =>
    isOpen
      ? null
      : css`
          height: 0;
          margin-bottom: 0;
          overflow: hidden;
          visibility: hidden;
          width: 0;
        `};
`;

const PopupArrow = styled.div`
  top: -${tombac.unit(4)};

  &::after {
    background-color: ${tombac.color.white};
    content: "";
    display: block;
    height: ${tombac.unit(8)};
    transform: rotate(45deg);
    width: ${tombac.unit(8)};
  }
`;

const PopupHeading = styled.div<PropsWithPropStyling>`
  ${tombac.altText({ fontWeight: "bold", fontSize: 14 })};
  ${propStyling}
`;

const ProductList = styled.ul`
  all: initial;
  display: grid;
  gap: ${tombac.space(1)};
  grid-template-columns: max-content max-content;
  margin-top: ${tombac.space(2)};
  visibility: inherit;
`;

const ProductListItem = styled.li`
  all: initial;
`;

const QuickLinkList = styled.ul`
  all: initial;
  display: block;
  margin-top: ${tombac.space(0.5)};
`;

const QuickLinkItem = styled.li`
  all: initial;
  display: block;
  margin: ${tombac.unit(0, -20)};
`;

const QuickLink = styled(Button).attrs<{ as: ElementType }>(() => ({
  variant: "flat",
}))<ButtonProps>`
  justify-content: flex-start;
  width: 100%;
  ${tombac.text({ fontSize: 12, fontWeight: 500 })}

  & > [class^="Button__Label"] {
    padding-top: 0.2rem; /* Workaround for Noway font */
  }
`;

const TooltipContent = styled.span`
  white-space: nowrap;
  ${tombac.text({ color: tombac.color.white })}
`;

const productOrder = ["ts", "rer", "od", "ctm"];

function compareProductOrder(a: UserProductSafe, b: UserProductSafe) {
  const aIndex = productOrder.findIndex((id) => a.id.startsWith(id));
  const bIndex = productOrder.findIndex((id) => b.id.startsWith(id));
  return aIndex === -1 && bIndex === -1
    ? a.displayName.localeCompare(b.displayName)
    : aIndex === -1
    ? 1
    : bIndex === -1
    ? -1
    : aIndex < bIndex
    ? -1
    : 1;
}
